/* eslint-disable @typescript-eslint/no-use-before-define */
import { Entity, SystemInterface } from '@goodboydigital/odie';
import { Container, Sprite } from 'pixi.js';

import { Shared } from '../../App';
import { EditorData, EditorEntityData, EditorEntityType, EditorSpriteData, EditorUtils } from '../../utils/EditorUtils';
import { Game } from '../Game';

export class ScenarioSystem implements SystemInterface
{
    public game: Game;
    public viewBg: Container;
    public viewFg: Container;
    public mounted = '';
    public items: ScenarioItem[];
    public zoomFactor = 0.25;
    
    public groundLoop: string = "";
    public groundConveyorLoop: string = "";
    public groundSlimeLoop: string = "";
    public frontGrass: string[] = []; 
    public frontFlowers: string[] = [];  
    public backBushes: string[] = [];  
    public backGrass: string[] = [];  
    public groundJunction: string = "";
    
    constructor(entity: Entity)
    {
        this.game = entity.scene as Game;
        this.viewBg = new Container();
        this.viewFg = new Container();
        this.items = [];

        this.game.physics.physicsFreeUpdateRunner.add(this as any);
    }

    public mountScenario(scenarioName: string): void
    {
        if (this.mounted === scenarioName) return;
        this.mounted = scenarioName;
        
        this.clear();

        const data = this.game.app.assets.getData<EditorData>(`scenario-${scenarioName}`);
        
        this.groundLoop = data.groundLoop;
        this.groundConveyorLoop = data.groundConveyorLoop;
        this.groundSlimeLoop = data.groundSlimeLoop;
        this.frontGrass = data.frontGrass;
        this.frontFlowers = data.frontFlowers;
        this.backBushes = data.backBushes;
        this.backGrass = data.backGrass;
        this.groundJunction = data.groundJunction;
        
        EditorUtils.scaleData(data, 0.5);

        for (const en of data.entities) this.addScenarioItem(en);

        this.game.view25d.container.addChildAt(this.viewBg, 0);
        this.game.view25d.container.addChild(this.viewFg);
    }

    private addScenarioItem(entity: EditorEntityData): void
    {
        if (entity.type === EditorEntityType.SPRITE)
        {
            const data = entity as EditorSpriteData;

            if (data.props?.ref) return;
            const m = typeof data.props?.m === 'number' ? data.props.m : 0;
            const v = typeof data.props?.v === 'number' ? data.props.v : 0;
            const repeatWidth = (data.props?.repeat as number) ?? 1000;
            const numSprites = m ? 4 : 1;

            for (let i = 0; i < numSprites; i++)
            {
                const item = new ScenarioItem(data.img);

                item.scale.x = data.sx;
                item.scale.y = data.sy;
                item.m = m;
                item.v = v;
                item.x = data.x + (repeatWidth * i) - repeatWidth;
                item.y = data.y;
                item.baseX = item.x;
                item.w = item.width - 1;
                item.repeatWidth = repeatWidth;

                this.items.push(item);
                if (item.m > 1)
                {
                    this.viewFg.addChild(item);
                }
                else
                {
                    this.viewBg.addChild(item);
                }
            }
        }
    }

    public physicsFreeUpdate(): void
    {
        if (!this.mounted) return;
        const zoomScale = (this.game.camera.camera.zoom * this.zoomFactor) + (1 - this.zoomFactor);
        const camera = this.game.camera;

        this.viewBg.scale.x = zoomScale;
        this.viewBg.scale.y = zoomScale;
        this.viewFg.scale.x = zoomScale;
        this.viewFg.scale.y = zoomScale;

        let i = this.items.length;
        const x = -this.game.camera.camera.x;

        while (i--)
        {
            const item = this.items[i];
            const l = item.x - (item.w / 2);
            const r = item.x + (item.w / 2);

            item.px += item.v;
            item.x = ((item.px + (x * item.m)) % item.repeatWidth) + item.baseX;
            item.renderable = r > camera.left && l < camera.right;
        }
    }

    public resize(): void
    {
        //
    }
    
    private clear(): void
    {
        for(var i = 0; i<this.items.length; i++){
            this.items[i].destroy();
        }
        
        this.items = [];
    }
}

class ScenarioItem extends Sprite
{
    public m = 0;
    public v = 0;
    public px = 0;
    public baseX = 0;
    public left = 0;
    public right = 0;
    public w = 0;
    public repeatWidth = 0;

    constructor(img: string)
    {
        super();
        this.texture = Shared.assets.getTexture(img);
        this.anchor.set(0.5);
    }
}
